import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import CircularProgress from "@material-ui/core/CircularProgress";
import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import CardTitle from "@saleor/components/CardTitle";
import { DateTime } from "@saleor/components/Date";
import Money from "@saleor/components/Money";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import StatusLabel from "@saleor/components/StatusLabel";
import { SearchCustomerOrders_search_edges_node } from "@saleor/searches/types/SearchCustomerOrders";
import useScrollableDialogStyle from "@saleor/styles/useScrollableDialogStyle";
import { FetchMoreProps } from "@saleor/types";
import React from "react";
import InfiniteScroll from "react-infinite-scroller";
import { FormattedMessage, useIntl } from "react-intl";

import { maybe, renderCollection, transformPaymentStatus } from "../../../misc";

const useStyles = makeStyles(
  {
    link: {
      cursor: "pointer"
    },
    textRight: {
      textAlign: "right"
    }
  },
  { name: "CustomerOrders" }
);

export interface CustomerOrdersProps extends FetchMoreProps {
  loading: boolean;
  orders: SearchCustomerOrders_search_edges_node[];
  onViewAllOrdersClick: () => void;
  onRowClick: (id: string) => void;
}

const CustomerOrders: React.FC<CustomerOrdersProps> = props => {
  const {
    hasMore,
    orders,
    onRowClick,
    onViewAllOrdersClick,
    onFetchMore
  } = props;
  const classes = useStyles(props);

  const scrollableDialogClasses = useScrollableDialogStyle({});
  const intl = useIntl();

  const orderList = orders
    ? orders.map(order => ({
        ...order,
        paymentStatus: transformPaymentStatus(order.paymentStatus, intl)
      }))
    : undefined;
  return (
    <Card>
      <CardTitle
        title={intl.formatMessage({
          defaultMessage: "Recent Orders",
          description: "section header"
        })}
        toolbar={
          <Button
            variant="text"
            color="primary"
            onClick={onViewAllOrdersClick}
            id="viewAllOrdersBtn"
          >
            <FormattedMessage
              defaultMessage="View all orders"
              description="button"
            />
          </Button>
        }
      />
      <div style={{ height: "330px", overflowY: "auto" }}>
        <InfiniteScroll
          pageStart={0}
          loadMore={onFetchMore}
          hasMore={hasMore}
          useWindow={false}
          loader={
            <div
              className={scrollableDialogClasses.loadMoreLoaderContainer}
              key={0}
            >
              <CircularProgress size={16} />
            </div>
          }
          threshold={10}
        >
          <ResponsiveTable key="table">
            <TableHead>
              <TableRow>
                <TableCell>
                  <FormattedMessage
                    defaultMessage="No. of Order"
                    description="number of order"
                  />
                </TableCell>
                <TableCell>
                  <FormattedMessage
                    defaultMessage="Date"
                    description="order placement date"
                  />
                </TableCell>
                <TableCell>
                  <FormattedMessage
                    defaultMessage="Status"
                    description="order status"
                  />
                </TableCell>
                <TableCell className={classes.textRight}>
                  <FormattedMessage
                    defaultMessage="Total"
                    description="order total amount"
                  />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {renderCollection(
                orderList,
                (order, index) => (
                  <TableRow
                    hover={!!order}
                    className={!!order ? classes.link : undefined}
                    onClick={order ? () => onRowClick(order.id) : undefined}
                    key={order ? order.id : "skeleton"}
                  >
                    <TableCell id={`noOfOrderRow${index}`}>
                      {maybe(() => order.number) ? (
                        "#" + order.number
                      ) : (
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell id={`dateRow${index}`}>
                      {maybe(() => order.created) ? (
                        <DateTime date={order.created} />
                      ) : (
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell id={`statusRow${index}`}>
                      {maybe(() => order.paymentStatus.status) !== undefined ? (
                        order.paymentStatus.status === null ? null : (
                          <StatusLabel
                            status={order.paymentStatus.status}
                            label={order.paymentStatus.localized}
                          />
                        )
                      ) : (
                        <Skeleton />
                      )}
                    </TableCell>
                    <TableCell
                      className={classes.textRight}
                      id={`totalRow${index}`}
                    >
                      {maybe(() => order.total.gross) ? (
                        <Money money={order.total.gross} />
                      ) : (
                        <Skeleton />
                      )}
                    </TableCell>
                  </TableRow>
                ),
                () => (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <FormattedMessage defaultMessage="No orders found" />
                    </TableCell>
                  </TableRow>
                )
              )}
            </TableBody>
          </ResponsiveTable>
        </InfiniteScroll>
      </div>
    </Card>
  );
};

CustomerOrders.displayName = "CustomerOrders";
export default CustomerOrders;
